import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../App';
import axios from 'axios';

/**
 * HomePage — Paranthaman 7.0
 *   Blue header (TopBar) → Saffron hero with large deity image → 
 *   Animated sliding panchangam carousel (±3 days) → Compact features
 *   = Indian Tricolor feel
 */

const DAYS_RANGE = 3; // ±3 days = 7 total slides
const AUTO_SLIDE_MS = 4000; // auto-slide every 4s

const tamilDays = ['ஞாயிற்றுக்கிழமை', 'திங்கட்கிழமை', 'செவ்வாய்க்கிழமை', 'புதன்கிழமை', 'வியாழக்கிழமை', 'வெள்ளிக்கிழமை', 'சனிக்கிழமை'];

// Build date info for a given day offset from today
const buildDateInfo = (offset, allData) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const engStr = `${dd}-${mm}-${d.getFullYear()}`;
  const key = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;

  const fallback = {
    line1: offset === 0 ? 'இன்று' : (offset > 0 ? `+${offset} நாள்` : `${offset} நாள்`),
    line2: `${engStr} - ${tamilDays[d.getDay()]}`,
    line3: '', isToday: offset === 0, offset
  };

  if (!allData) return fallback;
  const pd = allData.find(r => r.EnglishDate === key);
  if (!pd) return fallback;

  const pakshaAbbr = pd.PakshamNameInTamil?.includes('சுக்ல') ? 'சுக்.' : 'கிரு.';
  const thithi = pd.ThithiNameInTamil ? `${pakshaAbbr}${pd.ThithiNameInTamil}-${Number(pd.ThithiDuration).toFixed(2)}` : '';
  const star = pd.NakshatraNameInTamil ? `${pd.NakshatraNameInTamil} - ${Number(pd.NakshatraDuration).toFixed(2)}` : '';
  return {
    line1: `${pd.YearNameInTamil || 'பராபவ'} - ${pd.TamilMonth} - ${pd.TamilDate}`,
    line2: `${engStr} - ${pd.DayNameInTamil || tamilDays[d.getDay()]}`,
    line3: thithi && star ? `${thithi} | ${star}` : `${thithi}${star}`,
    isToday: offset === 0, offset
  };
};

const HomePage = () => {
  useAuth(); // keep context active
  const [slides, setSlides] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(DAYS_RANGE); // start at today (center)
  const [isAnimating, setIsAnimating] = useState(false);
  const [stats, setStats] = useState(null);
  const autoRef = useRef(null);
  const touchRef = useRef({ x: 0 });

  // Load data and build slides
  useEffect(() => {
    // Build fallback slides immediately
    const fallbackSlides = [];
    for (let i = -DAYS_RANGE; i <= DAYS_RANGE; i++) fallbackSlides.push(buildDateInfo(i, null));
    setSlides(fallbackSlides);

    axios.get('/data/panchangam_data-2025-to-2028.json')
      .then(res => {
        const rich = [];
        for (let i = -DAYS_RANGE; i <= DAYS_RANGE; i++) rich.push(buildDateInfo(i, res.data));
        setSlides(rich);
      })
      .catch(() => {});

    // Fetch site analytics
    const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:8000/api' : '/api';
    axios.get(`${API_URL}/analytics/public-stats`)
      .then(res => setStats(res.data))
      .catch(err => console.error("Could not fetch stats", err));
  }, []);

  // Auto-slide timer
  const resetAutoSlide = useCallback(() => {
    if (autoRef.current) clearInterval(autoRef.current);
    autoRef.current = setInterval(() => {
      setCurrentIdx(prev => (prev + 1) % (DAYS_RANGE * 2 + 1));
    }, AUTO_SLIDE_MS);
  }, []);

  useEffect(() => {
    resetAutoSlide();
    return () => clearInterval(autoRef.current);
  }, [resetAutoSlide]);

  const goTo = (idx) => {
    setIsAnimating(true);
    setCurrentIdx(idx);
    resetAutoSlide();
    setTimeout(() => setIsAnimating(false), 350);
  };

  const goPrev = () => goTo((currentIdx - 1 + slides.length) % slides.length);
  const goNext = () => goTo((currentIdx + 1) % slides.length);

  // Touch/swipe support
  const onTouchStart = (e) => { touchRef.current.x = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const diff = touchRef.current.x - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? goNext() : goPrev();
  };

  const current = slides[currentIdx] || {};

  const freeFeatures = [
    { icon: '⭐', label: 'நட்சத்திர பலன்', route: '/dayvalue' },
    { icon: '📅', label: 'பஞ்சாங்கம்', route: '/panchangam' },
    { icon: '🌟', label: 'வருட சிறப்பு', route: '/current-year' },
    { icon: '🕐', label: 'ராகு காலம்', route: '/diary' },
    { icon: '📜', label: 'சங்கல்பம்', route: '/sankalpam' },
    { icon: '⏰', label: 'ஓரை', route: '/horai' },
    { icon: '🔢', label: 'எண்கணிதம்', route: '/numerology' },
    { icon: '☀️', label: 'கௌரி பஞ்சாங்கம்', route: '/gowri-panchangam' },
    { icon: '📖', label: 'பஞ்சாங்க உறுப்பு', route: '/parts-of-panchangam' },
    { icon: '✨', label: 'நட்சத்திரம்', route: '/allaboutstars' },
    { icon: '⚠️', label: 'நாள் தோஷம்', route: '/day-dhosha' },
    { icon: '💑', label: 'தினப் பொருத்தம்', route: '/dhinaporutham' },
    { icon: '👶', label: 'ஜனன தீட்டு', route: '/gentheetu' },
    { icon: '🥀', label: 'மரண தீட்டு', route: '/marana-theetu' },
    { icon: '🌱', label: 'விவசாயம்', route: '/vivasayam' },
    { icon: '📚', label: 'FAQ', route: '/faq' },
    { icon: '📞', label: 'தொடர்பு', route: '/feedback' },
  ];

  const premiumFeatures = [
    { icon: '🔮', label: 'வாக்ய ஜாதகம்', route: '/casting' },
    { icon: '💍', label: 'ஜாதகப் பொருத்தம்', route: '/matching' },
    { icon: '📊', label: 'நாள் மதிப்பு Pro', route: '/dayvalue-premium' },
    { icon: '✅', label: 'சுப நாட்கள்', route: '/good-days' },
    { icon: '🕐', label: 'AI முகூர்த்தம்', route: '/my-muhurthams' },
  ];

  // Arrow button style
  const arrowBtn = {
    background: 'rgba(255,255,255,0.25)', border: 'none', borderRadius: '50%',
    width: '32px', height: '32px', fontSize: '1.1rem', cursor: 'pointer',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.2s', flexShrink: 0
  };

  return (
    <div style={{ background: 'linear-gradient(180deg, #ff9800 0%, #f57c00 25%, #ef6c00 50%, #fb8c00 70%, #ffb74d 85%, #388e3c 100%)', minHeight: '100vh' }}>

      {/* ══════ HERO — Large Paranthaman Image ══════ */}
      <section style={{ textAlign: 'center', padding: '16px 16px 8px' }}>
        <img src="/images/paranthaman.png" alt="ஸ்ரீ பரந்தாமன்"
          style={{
            height: '160px', maxWidth: '180px',
            display: 'block', margin: '0 auto 10px',
            borderRadius: '16px', padding: '8px',
            background: 'rgba(255,255,255,0.25)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }} />
        <h1 style={{
          color: '#fff', fontSize: '1.3rem', fontWeight: 900,
          margin: '0 0 2px', textShadow: '1px 1px 4px rgba(0,0,0,0.3)'
        }} className="tamil-heading">ஸ்ரீ பரந்தாமன் பஞ்சாங்கம்</h1>
        <p style={{ color: '#fff3e0', fontSize: '0.7rem', margin: '0 0 10px', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
          வாக்ய சித்தாந்தம் | Vakya Siddhanta Panchangam
        </p>
      </section>

      {/* ══════ ANIMATED SLIDING PANCHANGAM CAROUSEL ══════ */}
      <section style={{ padding: '0 10px 12px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          maxWidth: '420px', margin: '0 auto'
        }}>
          {/* Left Arrow */}
          <button onClick={goPrev} style={arrowBtn}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.4)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            aria-label="Previous day">◀</button>

          {/* Sliding Card */}
          <div style={{
            flex: 1, overflow: 'hidden', borderRadius: '14px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
          }}
          onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div style={{
              background: current.isToday
                ? 'rgba(255,255,255,0.95)'
                : 'rgba(255,255,255,0.82)',
              padding: '14px 16px', textAlign: 'center',
              transition: 'transform 0.35s ease, opacity 0.35s ease',
              transform: isAnimating ? 'scale(0.95)' : 'scale(1)',
              opacity: isAnimating ? 0.6 : 1,
            }}>
              {/* Today badge */}
              {current.isToday && (
                <span style={{
                  display: 'inline-block', background: '#e65100', color: '#fff',
                  fontSize: '0.6rem', fontWeight: 800, padding: '2px 10px',
                  borderRadius: '10px', marginBottom: '6px'
                }}>🕉️ இன்று / TODAY</span>
              )}
              {!current.isToday && (
                <span style={{
                  display: 'inline-block',
                  background: current.offset > 0 ? '#2e7d32' : '#6d1b1b',
                  color: '#fff', fontSize: '0.55rem', fontWeight: 700,
                  padding: '2px 8px', borderRadius: '10px', marginBottom: '6px'
                }}>{current.offset > 0 ? `+${current.offset} நாள்` : `${current.offset} நாள்`}</span>
              )}

              <div style={{ color: '#1b5e20', fontSize: '1.05rem', fontWeight: 900, lineHeight: 1.2 }} className="tamil-heading">
                {current.line1}
              </div>
              <div style={{ color: '#e65100', fontSize: '0.95rem', fontWeight: 800, marginTop: '4px' }}>
                {current.line2}
              </div>
              {current.line3 && (
                <div style={{ color: '#6d1b1b', fontSize: '0.78rem', fontWeight: 700, marginTop: '4px' }} className="tamil-text">
                  {current.line3}
                </div>
              )}
            </div>
          </div>

          {/* Right Arrow */}
          <button onClick={goNext} style={arrowBtn}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.4)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            aria-label="Next day">▶</button>
        </div>

        {/* Dot indicators */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', marginTop: '8px' }}>
          {slides.map((s, i) => (
            <button key={i} onClick={() => goTo(i)} style={{
              width: i === currentIdx ? '18px' : '7px', height: '7px',
              borderRadius: '4px', border: 'none', cursor: 'pointer',
              background: i === currentIdx ? '#fff' : (s.isToday ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.35)'),
              transition: 'all 0.3s'
            }} aria-label={`Day ${s.offset}`} />
          ))}
        </div>

        {/* Full panchangam link */}
        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <Link to="/panchangam" style={{
            display: 'inline-block', padding: '6px 18px',
            background: 'linear-gradient(90deg, #1b5e20, #2e7d32)', color: 'white',
            borderRadius: '20px', fontWeight: 800, fontSize: '0.75rem',
            textDecoration: 'none', boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
          }}>முழு பஞ்சாங்கம் காண →</Link>
        </div>
      </section>

      {/* ══════ Free Features — compact icons ══════ */}
      <section style={{ padding: '4px 8px', maxWidth: '500px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '0.9rem', fontWeight: 900, color: '#fff', margin: '0 0 6px 4px', textShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
          🎋 இலவச சேவைகள்
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '2px' }}>
          {freeFeatures.map((f, i) => (
            <Link key={i} to={f.route} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
              padding: '7px 2px', textDecoration: 'none', borderRadius: '8px',
              transition: 'background 0.15s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{f.icon}</span>
              <span style={{ fontSize: '0.58rem', fontWeight: 800, color: '#fff', textAlign: 'center', lineHeight: 1.15, textShadow: '0 1px 2px rgba(0,0,0,0.25)' }} className="tamil-text">{f.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ══════ Premium — compact ══════ */}
      <section style={{ padding: '6px 8px 4px', maxWidth: '500px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '0.85rem', fontWeight: 900, color: '#fff3e0', margin: '0 0 6px 4px', textShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
          ⭐ சிறப்பு வசதிகள்
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px' }}>
          {premiumFeatures.map((f, i) => (
            <Link key={i} to={f.route} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
              padding: '7px 2px', textDecoration: 'none', borderRadius: '8px',
              transition: 'background 0.15s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>{f.icon}</span>
              <span style={{ fontSize: '0.58rem', fontWeight: 800, color: '#fff', textAlign: 'center', lineHeight: 1.15, textShadow: '0 1px 2px rgba(0,0,0,0.25)' }} className="tamil-text">{f.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ══════ PUBLIC SITE ANALYTICS ══════ */}
      {stats && (
        <section style={{ padding: '16px 8px 4px', maxWidth: '500px', margin: '0 auto', marginTop: '12px' }}>
          <h2 style={{ fontSize: '0.95rem', fontWeight: 900, color: '#fff', margin: '0 0 10px 4px', textShadow: '0 1px 3px rgba(0,0,0,0.2)', textAlign: 'center' }}>
            🌟 எங்கள் ஆன்மீக குடும்பம்
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '12px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(4px)' }}>
              <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#fff', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                {stats.total_qa?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#fff3e0', marginTop: '2px', textTransform: 'uppercase' }}>கேள்வி-பதில்கள் (Q&A)</div>
            </div>
            
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '12px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(4px)' }}>
              <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#fff', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                {stats.total_comments?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#fff3e0', marginTop: '2px', textTransform: 'uppercase' }}>மொத்த உரையாடல்கள்</div>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '12px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(4px)' }}>
              <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#fff', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                {stats.total_posts?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#fff3e0', marginTop: '2px', textTransform: 'uppercase' }}>ஆன்மீகக் கட்டுரைகள்</div>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '12px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(4px)', position: 'relative' }}>
              <span className="absolute top-2 right-2 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#fff', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                {stats.active_users || 0}
              </div>
              <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#fff3e0', marginTop: '2px', textTransform: 'uppercase' }}>தற்போது தளத்தில்</div>
            </div>
          </div>
        </section>
      )}

      {/* ══════ Trust line ══════ */}
      <div style={{ textAlign: 'center', padding: '8px 12px 14px', fontSize: '0.6rem', color: '#fff', fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
        🔬 சாஸ்திர விதிகளின்படி • 🤖 நவீன தொழில்நுட்பம் • 🏆 12+ ஆண்டுகள் சேவை
      </div>
    </div>
  );
};

export default HomePage;
